import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import {
  getRules,
  getBehaviors,
  getPatterns,
  getTrades,
  getDailyLessons,
  getJarvisMemoryData,
  getStats,
  getRiskSettings,
  getCurrentPrice,
  getStorageSizeMB,
  isStorageNearLimit,
} from '../services/storage';
import { getBrainReport } from '../engines/jarvisBrain';
import { getCurrentSession } from '../utils/dateUtils';
import { bootstrapJarvisMemory } from '../services/jarvisAutoLearn';

// ─── Initial state ─────────────────────────────────────────────────────
const initialState = {
  // JARVIS AI state
  isThinking:   false,
  lastError:    null,

  // Conversation
  messages:     [],      // [{ role: 'user'|'assistant', content, timestamp }]
  inputText:    '',

  // Live market data
  currentPrice: null,
  priceChange:  null,    // +/- from last fetch
  currentSession: null,
  priceLoading: false,
  priceError:   null,

  // JARVIS knowledge (refreshed from storage)
  rules:         [],
  behaviors:     [],
  patterns:      [],
  trades:        [],
  lessons:       [],
  memory:        {},
  stats:         {},
  riskSettings:  {},
  brainReport:   null,

  // UI state
  activeScreen: 'v5home',  // 'v5home' | 'v5chat' | 'dashboard' | 'knowledge' | 'rules' | 'ict' | 'journal' | 'risk' | 'stats' | 'memory' | 'psychology' | 'bridge'
  sidebarOpen:  false,
  theme:        'dark',

  // Storage health
  storageMB:    0,
  storageAlert: false,
};

// ─── Action types ──────────────────────────────────────────────────────
export const A = {
  // AI
  THINKING_START:   'THINKING_START',
  THINKING_STOP:    'THINKING_STOP',
  SET_ERROR:        'SET_ERROR',
  CLEAR_ERROR:      'CLEAR_ERROR',

  // Messages
  ADD_MESSAGE:      'ADD_MESSAGE',
  CLEAR_MESSAGES:   'CLEAR_MESSAGES',
  SET_INPUT:        'SET_INPUT',

  // Price
  PRICE_LOADING:    'PRICE_LOADING',
  PRICE_UPDATE:     'PRICE_UPDATE',
  PRICE_ERROR:      'PRICE_ERROR',

  // Knowledge refresh
  REFRESH_DATA:     'REFRESH_DATA',

  // UI
  SET_SCREEN:       'SET_SCREEN',
  TOGGLE_SIDEBAR:   'TOGGLE_SIDEBAR',
  SET_STORAGE_INFO: 'SET_STORAGE_INFO',
};

// ─── Reducer ────────────────────────────────────────────────────────────
const jarvisReducer = (state, action) => {
  switch (action.type) {
    case A.THINKING_START:
      return { ...state, isThinking: true, lastError: null };
    case A.THINKING_STOP:
      return { ...state, isThinking: false };
    case A.SET_ERROR:
      return { ...state, isThinking: false, lastError: action.payload };
    case A.CLEAR_ERROR:
      return { ...state, lastError: null };

    case A.ADD_MESSAGE: {
      const msg = {
        id:        `msg_${Date.now()}`,
        timestamp: new Date().toISOString(),
        ...action.payload,
      };
      return { ...state, messages: [...state.messages, msg] };
    }
    case A.CLEAR_MESSAGES:
      return { ...state, messages: [] };
    case A.SET_INPUT:
      return { ...state, inputText: action.payload };

    case A.PRICE_LOADING:
      return { ...state, priceLoading: true, priceError: null };
    case A.PRICE_UPDATE: {
      const prev = state.currentPrice;
      return {
        ...state,
        priceLoading:   false,
        priceError:     null,
        currentPrice:   action.payload.price,
        priceChange:    prev ? action.payload.price - prev : 0,
        currentSession: getCurrentSession(),
      };
    }
    case A.PRICE_ERROR:
      return { ...state, priceLoading: false, priceError: action.payload };

    case A.REFRESH_DATA:
      return {
        ...state,
        rules:        action.payload.rules,
        behaviors:    action.payload.behaviors,
        patterns:     action.payload.patterns,
        trades:       action.payload.trades,
        lessons:      action.payload.lessons,
        memory:       action.payload.memory,
        stats:        action.payload.stats,
        riskSettings: action.payload.riskSettings,
        brainReport:  action.payload.brainReport,
      };

    case A.SET_SCREEN:
      return { ...state, activeScreen: action.payload, sidebarOpen: false };
    case A.TOGGLE_SIDEBAR:
      return { ...state, sidebarOpen: !state.sidebarOpen };
    case A.SET_STORAGE_INFO:
      return { ...state, storageMB: action.payload.mb, storageAlert: action.payload.alert };

    default:
      return state;
  }
};

// ─── Context ────────────────────────────────────────────────────────────
const JarvisContext = createContext(null);

// ─── Provider ────────────────────────────────────────────────────────────
export const JarvisProvider = ({ children }) => {
  const [state, dispatch] = useReducer(jarvisReducer, initialState);

  // Load all data from localStorage into context
  const refreshData = useCallback(() => {
    dispatch({
      type: A.REFRESH_DATA,
      payload: {
        rules:       getRules(),
        behaviors:   getBehaviors(),
        patterns:    getPatterns(),
        trades:      getTrades(),
        lessons:     getDailyLessons(),
        memory:      getJarvisMemoryData(),
        stats:       getStats(),
        riskSettings: getRiskSettings(),
        brainReport: getBrainReport(),
      },
    });

    // Storage health
    const mb    = getStorageSizeMB();
    const alert = isStorageNearLimit();
    dispatch({ type: A.SET_STORAGE_INFO, payload: { mb, alert } });
  }, []);

  // Bootstrap on mount
  useEffect(() => {
    // Sync knowledge chunks → strategies + refresh all panels
    bootstrapJarvisMemory();
    refreshData();

    // Pull saved price from storage on boot (getCurrentPrice returns a number)
    const saved = getCurrentPrice();
    if (saved > 0) {
      dispatch({ type: A.PRICE_UPDATE, payload: { price: saved } });
    }
  }, [refreshData]);

  const value = { state, dispatch, refreshData };

  return (
    <JarvisContext.Provider value={value}>
      {children}
    </JarvisContext.Provider>
  );
};

// ─── Consumer hook ─────────────────────────────────────────────────────
export const useJarvisContext = () => {
  const ctx = useContext(JarvisContext);
  if (!ctx) throw new Error('useJarvisContext must be used inside JarvisProvider');
  return ctx;
};
